import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  Signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDrawer } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SpinnerModule } from '@jfw/angular/ecommerce/common-components/spinner';
import { AccountService } from '@jfw/angular/shared/services';
import {
  convertDateToPrettyDay,
  convertISOToPrettyTime,
  convertISOToPrettyTimeLocal,
  getShortHandCarrierService,
} from '@jfw/business-logic';
import {
  Carrier,
  DeliveryDays,
  DeliveryVans,
  ParcelCarrier,
  WarehouseLocationType,
} from '@jfw/types';
@Component({
  selector: 'app-shipping-info',
  templateUrl: './shipping-info.component.html',
  styleUrls: ['./shipping-info.component.scss'],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    SpinnerModule,
  ],
})
export class ShippingInfoComponent {
  private accountService: AccountService = inject(AccountService);
  @Input() drawer?: MatDrawer;

  platformAccount = this.accountService._platformAccount;
  drawerState = computed(() => this.accountService.drawerState());
  currentSelectedWarehouse: WarehouseLocationType | undefined;
  defaultWarehouse = computed(() => {
    const serviceCenter = this.accountService._platformAccount()?.serviceCenter;
    this.currentSelectedWarehouse = this.warehouseOptions.find(
      (option) => option.cityCode === serviceCenter,
    );
    return this.warehouseOptions.find(
      (option) => option.cityCode === serviceCenter,
    );
  });

  preferredCarrier = this.accountService._preferredCarrier;
  returnCutOffs = this.accountService._returnCutOffs;

  parcelInfo: Signal<ParcelCarrier | undefined> = computed(() => {
    const parcelCarrier = this.accountService._parcelCarrier();
    return parcelCarrier;
  });

  shippingInstructions = this.accountService._shippingInstructions;
  groupedCarriers = computed(() => {
    const carriers = this.parcelInfo() ?? [];
    // group by arrival time (or some other row-defining key)
    const groups: Record<
      string,
      {
        upsPri?: Carrier;
        fedexPri?: Carrier;
        upsStd?: Carrier;
        fedexStd?: Carrier;
        upsSat?: Carrier;
        fedexSat?: Carrier;
      }
    > = {};
    for (const carrier of carriers) {
      const key = carrier.arrival; // grouping by arrival time
      if (!groups[key]) groups[key] = {};

      // Next Day Pri
      if (
        carrier.carrier === 'UPS' &&
        carrier.carrierService === 'Next Day Air'
      ) {
        groups[key].upsPri = carrier;
      }
      if (
        carrier.carrier === 'Fedex' &&
        carrier.carrierService === 'Priority Overnight'
      ) {
        groups[key].fedexPri = carrier;
      }

      // Next Day Std
      if (
        carrier.carrier === 'UPS' &&
        carrier.carrierService === 'Next Day Air Saver'
      ) {
        groups[key].upsStd = carrier;
      }
      if (
        carrier.carrier === 'Fedex' &&
        carrier.carrierService === 'Standard Overnight'
      ) {
        groups[key].fedexStd = carrier;
      }

      // Saturday
      if (
        carrier.carrier === 'UPS' &&
        carrier.carrierService === 'Next Day Air Saver'
      ) {
        groups[key].upsSat = carrier;
      }
      if (
        carrier.carrier === 'Fedex' &&
        carrier.carrierService === 'Standard Overnight'
      ) {
        groups[key].fedexSat = carrier;
      }
    }

    return groups;
  });

  groupedCarriersArray = computed(() => {
    const carriers = this.parcelInfo() ?? [];
    // define rows in order
    const rows: Array<{
      label: string;
      ups?: Carrier;
      fedex?: Carrier;
    }> = [
      { label: 'Next Day Priority' },
      { label: 'Next Day Standard' },
      { label: 'Saturday' },
    ];
    for (const carrier of carriers) {
      if (carrier.carrier === 'UPS') {
        if (carrier.carrierService === 'Next Day Air') {
          rows[0].ups = carrier; // Next Day Pri
        }
        if (carrier.carrierService === 'Next Day Air Saver') {
          rows[1].ups = carrier; // Next Day Std
        }
        if (carrier.carrierService === 'Next Day Air (Saturday)') {
          rows[2].ups = carrier; // Saturday
        }
      }

      if (carrier.carrier === 'Fedex') {
        if (carrier.carrierService === 'Priority Overnight') {
          rows[0].fedex = carrier; // Next Day Pri
        }
        if (carrier.carrierService === 'Standard Overnight') {
          rows[1].fedex = carrier; // Next Day Std
        }
        if (carrier.carrierService === 'Priority Overnight (Saturday)') {
          rows[2].fedex = carrier; // Saturday
        }
      }
    }

    return rows;
  });

  // EXT-6537: back to a Service / UPS / FedEx grid, per the dashboard design.
  // The drawer type is larger now, and these cells hold short values
  // ("10:30 AM (NDA)"), so nothing is ellipsised at the default width.
  cutoffRows = computed(() =>
    this.groupedCarriersArray().map((row) => {
      const cell = (carrier: Carrier | undefined) =>
        carrier
          ? `${convertISOToPrettyTime(carrier.arrival)} (${getShortHandCarrierService(
              carrier.carrierService,
            )})`
          : '\u2014';

      return { label: row.label, ups: cell(row.ups), fedex: cell(row.fedex) };
    }),
  );

  deliveryDays: Signal<DeliveryDays | undefined> = computed(() => {
    const deliverDays = this.accountService._deliveryDays();
    return deliverDays;
  });

  deliveryVans: Signal<DeliveryVans | undefined> = computed(() => {
    return this.accountService._deliveryVans();
  });

  parcelLoader = computed(() => this.accountService.parcelLoader());
  deliveryVanLoader = computed(() => this.accountService.deliveryVanLoader());
  deliveryDaysLoader = computed(() => this.accountService.deliveryDaysLoader());
  preferredCarrierLoader = computed(() =>
    this.accountService.preferredCarrierLoader(),
  );
  shippingInstructionsLoader = computed(() =>
    this.accountService.setShippingInstructionsLoader(),
  );

  // Drives the refresh button's spin state — true while any of the 5 shipping
  // fetches are in flight, whether triggered by mount, the timer, or manualRefresh().
  isRefreshing = computed(
    () =>
      this.parcelLoader() ||
      this.deliveryDaysLoader() ||
      this.deliveryVanLoader() ||
      this.preferredCarrierLoader() ||
      this.shippingInstructionsLoader(),
  );

  warehouseOptions: WarehouseLocationType[] = [
    { city: 'Visalia', cityCode: 'VI', state: 'CA', timezone: 'PST' },
    { city: 'Anderson', cityCode: 'AN', state: 'IN', timezone: 'EST' },
    { city: 'Salina', cityCode: 'KS', state: 'KS', timezone: 'CST' },
    { city: 'Trenton (Rental)', cityCode: 'TR', state: 'IL', timezone: 'CST' },
    {
      city: 'Trenton (Purchase)',
      cityCode: 'OT',
      state: 'IL',
      timezone: 'CST',
    },
    { city: 'Atlanta', cityCode: 'GA', state: 'GA', timezone: 'EST' },
    { city: 'Richmond', cityCode: 'RI', state: 'VA', timezone: 'EST' },
    { city: 'Seguin', cityCode: 'TX', state: 'TX', timezone: 'CST' },
  ];

  private readonly destroyRef = inject(DestroyRef);

  // Production can change cutoffs/carrier data underneath a long-open drawer, so this
  // re-fetches on a timer rather than relying solely on the initial load below.
  private static readonly REFRESH_INTERVAL_MS = 5 * 60 * 1000;

  constructor() {
    // Re-opening the Shipping tab remounts this component (it's behind an @if in the
    // parent), which used to re-trigger this same effect and refetch everything on
    // every click into the tab. Since the underlying data lives on AccountService (not
    // this component) and survives that remount, skip the fetch here when we've
    // already loaded for this exact account+warehouse — only a genuine warehouse
    // change (selectedWarehouse(), below) or the timer/manual refresh should refetch.
    effect(() => {
      const account = this.platformAccount();
      const warehouse = untracked(() => this.defaultWarehouse()?.cityCode);
      if (
        account?.accountNumber &&
        warehouse &&
        !this.accountService.hasLoadedShippingFor(
          account.accountNumber,
          warehouse,
        )
      ) {
        this.refreshShippingData(account, warehouse);
      }
    });

    interval(ShippingInfoComponent.REFRESH_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const account = this.platformAccount();
        const warehouse = this.defaultWarehouse()?.cityCode;
        if (account && warehouse) {
          this.refreshShippingData(account, warehouse);
        }
      });
  }

  // Bound to the panel's manual refresh control — always refetches regardless of
  // hasLoadedShippingFor, same as the 5-minute timer.
  manualRefresh(): void {
    const account = this.platformAccount();
    const warehouse = this.defaultWarehouse()?.cityCode;
    if (account && warehouse) {
      this.refreshShippingData(account, warehouse);
    }
  }

  private refreshShippingData(
    account: NonNullable<ReturnType<typeof this.platformAccount>>,
    warehouse: string,
  ): void {
    const { accountNumber } = account;
    if (!accountNumber) return;

    this.accountService.markShippingLoaded(accountNumber, warehouse);
    this.accountService.setParcelCarrierInfo(
      warehouse,
      account.address?.zip ?? '',
    );
    this.accountService.setDeliveryDays(warehouse);
    this.accountService.setDeliveryVans(accountNumber, warehouse);
    this.accountService.setPreferredCarrier(accountNumber);
    this.accountService.setShippingInstructions(accountNumber);
  }

  ngOnInit() {}

  async selectedWarehouse(warehouseSelected: string) {
    const warehouse = this.warehouseOptions.find((option) => {
      if (option.city === warehouseSelected) {
        return option;
      }
      return undefined;
    });
    const platformAccount = this.platformAccount();
    const accountNumber = platformAccount?.accountNumber;
    if (warehouse && platformAccount && accountNumber) {
      this.currentSelectedWarehouse = warehouse;
      this.accountService.setParcelCarrierInfo(
        warehouse.cityCode,
        this.platformAccount()?.address?.zip ?? '',
      );
      this.accountService.setDeliveryDays(warehouse.cityCode);
      this.accountService.setDeliveryVans(
        this.platformAccount()?.accountNumber ?? '',
        warehouse.cityCode,
      );
      this.accountService.setShippingInstructions(accountNumber);
    }
  }

  isHotshotTime(hotshotTimeStart: number, hotshotTimeEnd: number) {
    const now = Date.now() / 1000; // current time in ms
    return now >= hotshotTimeStart && now <= hotshotTimeEnd;
  }

  convertDateToPrettyDay(dateString: string) {
    return convertDateToPrettyDay(dateString);
  }

  convertISOToPrettyTime(isoTime: string) {
    return convertISOToPrettyTime(isoTime);
  }

  convertISOToPrettyTimeLocal(isoTime: string, state: string) {
    return convertISOToPrettyTimeLocal(isoTime);
  }

  getShortHandCarrierService(carrierService: string) {
    return getShortHandCarrierService(carrierService);
  }

  getCarrierShipDays(carrierName: string, serviceGroup: string) {
    let days = '';
    this.parcelInfo()?.forEach((parcel) => {
      if (
        parcel.carrier === carrierName &&
        parcel.carrierService === serviceGroup
      ) {
        days = String(parcel.days);
      }
    });
    return days ? `(${days})` : '';
  }

  getCutoffStatus(dayString: string | null | undefined): string {
    const DEFAULT_STATUS = 'not set';

    if (!dayString || dayString.trim() === '') {
      return DEFAULT_STATUS;
    }

    const parts = dayString.split(':');
    if (parts.length < 2) return DEFAULT_STATUS;

    let hour24 = parseInt(parts[0], 10);
    const minutes = parts[1]; // Captures the "00"

    if (isNaN(hour24)) return DEFAULT_STATUS;

    const period = hour24 >= 12 ? 'pm' : 'am';

    let hour12 = hour24 % 12;
    hour12 = hour12 === 0 ? 12 : hour12;

    return `${hour12}:${minutes}${period}`;
  }

  // EXT-6207: returns the bare code ("CST"). The "Times in " prefix is the
  // template's, matching the mockup's "Times in CST" -- this used to return the
  // whole phrase, which read as "Times in times in (CST)" once the badge gained
  // its own label. The dealer-portal component of the same name has its own,
  // separate copy of this method; it is unaffected.
  getTimezone() {
    return this.currentSelectedWarehouse?.timezone ?? '';
  }
  /**
   *
   * @param carrierName UP, FX
   * @param serviceGroup GROUND, AIR
   * @returns mat-icon
   */
  getCarrierShipStatus(carrierName: string, serviceGroup: string) {
    let iconInfo = {
      icon: 'fiber_manual_record',
      toolTip: '',
      colorClass: 'green',
    };
    this.deliveryDays()?.forEach((carrier) => {
      if (
        carrierName === carrier.carrier &&
        serviceGroup === carrier.serviceGroup
      ) {
        const status = carrier.shipStatus;
        if (status === 'Open') {
          iconInfo.colorClass = 'green';
          iconInfo.toolTip =
            carrier.message === 'Unknown' ? '' : carrier.message;
        }
        if (status === 'Closed') {
          iconInfo.colorClass = 'red';
          // iconInfo.toolTip = carrier.message;
          iconInfo.toolTip = 'Closed';
        }
        if (status === 'Hot Shot') {
          iconInfo.colorClass = 'yellow';
          iconInfo.toolTip = carrier.message;
        }
      }
    });

    return iconInfo;
  }
}
